import { liteClient as algoliasearch, type LiteClient } from "algoliasearch/lite";
import type { SortByItem } from "instantsearch.js/es/connectors/sort-by/connectSortBy";
import { createContext, type FC, type PropsWithChildren, useContext, useMemo, useState } from "react";
import { parseJson } from "./utils/parseJson";

interface Config {
    algoliaConfig: Omit<AlgoliaConfig, "tc">;
    tc: Record<string, string>;
    algoliaRefinements: AlgoliaRefinements;
    recommendationIds?: string[];
    searchClient: LiteClient;
    sorting: SortByItem[];
    formater?: GlobalFormat;
    indexPrefix: string;
}

const ConfigContext = createContext<Config | undefined>(undefined);

interface ConfigProviderProps {
    domElement?: HTMLElement;
    initialConfig?: AlgoliaConfig;
    initialRefinements?: AlgoliaRefinements;
}

function parseMaybeJson(raw?: string | any): any | undefined {
    if (!raw) return undefined;
    if (typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return undefined;
    try { return JSON.parse(raw); } catch { return undefined; }
}

function normalizeRefinements(input: any): AlgoliaRefinements {
    let parsed = input;
    if (typeof input === 'string') {
        try {
            parsed = JSON.parse(input);
        } catch {
            parsed = input;
        }
    }

    const list = Array.isArray(parsed?.refinementList)
        ? parsed.refinementList
        : Array.isArray(parsed)
            ? parsed
            : [];

    return { refinementList: list, hasRefinements: list.length > 0 };
}

type ShopSortingItem = {
    label: string;
    index: string;
    code?: string;
};

export const ConfigProvider: FC<PropsWithChildren<ConfigProviderProps>> = ({
                                                                               children,
                                                                               domElement,
                                                                               initialConfig,
                                                                               initialRefinements
                                                                           }) => {
    const configFromDom = (domElement?.dataset?.config
        ? (parseJson(domElement.dataset.config) as AlgoliaConfig | null)
        : undefined) ?? null;

    const configFromWindow =
        typeof window !== "undefined"
            ? ((window as any).__CONFIG__ as AlgoliaConfig | undefined)
            : undefined;

    const cfg = (configFromDom ?? initialConfig ?? configFromWindow) || null;

    if (!cfg) {
        console.error("[ConfigProvider] missing configuration: no data-config, no initialConfig, no window.__CONFIG__");
        throw new Error("missing configuration");
    }

    if (!cfg.algoliaAppId || !cfg.algoliaSearchKey || !cfg.algoliaIndexName) {
        console.error("[ConfigProvider] invalid Algolia credentials in cfg:", {
            appId: cfg.algoliaAppId, hasKey: !!cfg.algoliaSearchKey, index: cfg.algoliaIndexName
        });
        throw new Error("Algolia config missing (appId/searchKey/indexName)");
    }

    const refinementsFromDom = parseMaybeJson(domElement?.dataset?.refinements);
    const refinementsFromWindow = typeof window !== "undefined" ? parseMaybeJson((window as any).__REFINEMENTS__) : undefined;


    const refinements = normalizeRefinements(
        initialRefinements ?? refinementsFromDom ?? refinementsFromWindow
    );


    const { tc: translations = {}, sorting: shopSorting = [] } = cfg;

    const indexPrefix = (configFromDom as any)?.indexPrefix || "";
    const [formater] = useState<GlobalFormat | undefined>(
        typeof window !== "undefined" ? (window as any)?.GLOBALS?.format : undefined
    );
    const [recommendationIds] = useState(
        parseJson(domElement?.dataset?.recommendationIds)
    );

    const value = useMemo<Config>(() => {
        const searchClient = algoliasearch(cfg.algoliaAppId, cfg.algoliaSearchKey);

        const sorting: SortByItem[] = (shopSorting as ShopSortingItem[]).map(
            (s: ShopSortingItem) => ({
                label: s.label,
                value: s.index,
                code: s.code
            })
        );

        const { tc: _ignoreTc, ...algoliaConfig } = cfg as any;

        return {
            algoliaConfig,
            algoliaRefinements: refinements as AlgoliaRefinements,
            searchClient,
            tc: translations as Record<string, string>,
            recommendationIds,
            sorting,
            indexPrefix,
            formater
        };
    }, [cfg, refinements, translations, shopSorting, indexPrefix, formater]);

    return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

export const useConfigContext = () => {
    const context = useContext(ConfigContext);
    if (!context) throw new Error("useConfigContext must be used within a ConfigProvider");
    return context;
};